import { create } from 'zustand';
import quotesService, { Quote, QuoteInput, ListQuotesParams } from '../services/supplierCrm/quotesService';

interface SupplierQuotesState {
  quotes: Quote[];
  quotesCount: number | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  fetchQuotes: (params: ListQuotesParams) => Promise<void>;
  createQuote: (payload: QuoteInput) => Promise<Quote>;
  updateQuote: (id: string, payload: Partial<QuoteInput> & { organization_id: string }) => Promise<Quote>;
  deleteQuote: (id: string, organization_id: string) => Promise<void>;
  setError: (message: string | null) => void;
  reset: () => void;
}

export const useSupplierQuotesStore = create<SupplierQuotesState>((set, get) => ({
  quotes: [],
  quotesCount: null,
  loading: false,
  saving: false,
  error: null,
  
  async fetchQuotes(params) {
    set({ loading: true, error: null });
    try {
      const { data, count } = await quotesService.list(params);
      set({ quotes: data ?? [], quotesCount: count ?? null });
    } catch (err: any) {
      set({ error: err?.message || 'Failed to load quotes' });
      throw err;
    } finally {
      set({ loading: false });
    }
  },
  
  async createQuote(payload) {
    set({ saving: true, error: null });
    try {
      const { data } = await quotesService.create(payload);
      const { quotes } = get();
      set({ quotes: [data, ...quotes] });
      return data;
    } catch (err: any) {
      set({ error: err?.message || 'Failed to create quote' });
      throw err;
    } finally {
      set({ saving: false });
    }
  },
  
  async updateQuote(id, payload) {
    set({ saving: true, error: null });
    try {
      const { data } = await quotesService.update(id, payload);
      const { quotes } = get();
      set({ quotes: quotes.map((q) => (q.id === data.id ? data : q)) });
      return data;
    } catch (err: any) {
      set({ error: err?.message || 'Failed to update quote' });
      throw err;
    } finally {
      set({ saving: false });
    }
  },
  
  async deleteQuote(id, organization_id) {
    set({ saving: true, error: null });
    try {
      await quotesService.remove(id, organization_id);
      const { quotes } = get();
      set({ quotes: quotes.filter((q) => q.id !== id) });
    } catch (err: any) {
      set({ error: err?.message || 'Failed to delete quote' });
      throw err;
    } finally {
      set({ saving: false });
    }
  },
  
  setError(message) {
    set({ error: message });
  },
  
  reset() {
    set({
      quotes: [],
      quotesCount: null,
      loading: false,
      saving: false,
      error: null
    });
  }
}));
